import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import FAQSection from "@/components/ui/faq-section";
import { AuroraHero } from "@/components/ui/aurora-hero";
import { 
  DollarSign, 
  ShoppingCart, 
  Building2, 
  CreditCard,
  Receipt,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  MonitorPlay,
  Users,
  Clock,
  Target,
  Calculator,
  FileSpreadsheet,
  Wallet,
  HandCoins,
  PiggyBank,
  BarChart3,
  Banknote,
  Globe,
  Archive
} from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";

const ServicesFinanceiro = () => {
  const [activeTab, setActiveTab] = useState("compras");

  const services = [
    {
      id: "compras",
      icon: ShoppingCart,
      title: "Solicitação de Compras",
      description: "Portal completo para solicitação, aprovação e acompanhamento de compras",
      color: "from-fs-purple-500 to-fs-cyan-500",
      details: {
        overview: "Centralize e automatize todo o processo de compras da sua empresa com um portal intuitivo que permite solicitações, aprovações hierárquicas e acompanhamento em tempo real, garantindo controle total dos gastos e compliance com políticas internas.",
        features: [
          "Portal de solicitação intuitivo",
          "Catálogo de produtos e serviços",
          "Workflow de aprovação hierárquica",
          "Controle orçamentário automático",
          "Integração com fornecedores",
          "Relatórios de gastos em tempo real"
        ],
        benefits: [
          "Redução de 70% no tempo de aprovação",
          "Controle total do orçamento",
          "Transparência nos processos",
          "Eliminação de compras não autorizadas"
        ],
        processes: [
          "Solicitação pelo requisitante",
          "Validação orçamentária",
          "Aprovação hierárquica",
          "Cotação com fornecedores",
          "Autorização final",
          "Acompanhamento da entrega"
        ]
      }
    },
    {
      id: "fornecedores",
      icon: Building2,
      title: "Cadastro de Fornecedores",
      description: "Sistema de autosserviço para cadastro e qualificação de fornecedores",
      color: "from-fs-cyan-500 to-fs-magenta-500",
      details: {
        overview: "Permita que fornecedores se cadastrem de forma autônoma através de um portal dedicado, com validação automática de documentos, qualificação técnica e acompanhamento de performance, mantendo sua base sempre atualizada e qualificada.",
        features: [
          "Portal de autosserviço para fornecedores",
          "Validação automática de documentos",
          "Qualificação técnica e financeira",
          "Acompanhamento de performance",
          "Renovação automática de documentos",
          "Avaliação contínua de fornecedores"
        ],
        benefits: [
          "Base de fornecedores sempre atualizada",
          "Redução de riscos contratuais",
          "Processo de qualificação automatizado",
          "Melhoria na qualidade dos fornecimentos"
        ],
        processes: [
          "Cadastro inicial pelo fornecedor",
          "Validação documental",
          "Qualificação técnica",
          "Aprovação financeira",
          "Homologação final",
          "Monitoramento contínuo"
        ]
      }
    },
    {
      id: "reembolsos",
      icon: CreditCard,
      title: "Reembolso de Despesas",
      description: "Gestão completa de solicitações de reembolso com aprovação digital",
      color: "from-fs-magenta-500 to-fs-purple-500",
      details: {
        overview: "Simplifique o processo de reembolso de despesas com um sistema digital que permite anexar comprovantes, validar automaticamente as políticas da empresa e acompanhar o status da solicitação até o pagamento final.",
        features: [
          "Solicitação digital com anexos",
          "Validação automática de políticas",
          "Aprovação por workflow",
          "Integração com cartões corporativos",
          "Controle de quilometragem",
          "Pagamento automático via sistema"
        ],
        benefits: [
          "Reembolso até 80% mais rápido",
          "Compliance com políticas internas",
          "Eliminação de papelada",
          "Visibilidade total dos gastos"
        ],
        processes: [
          "Solicitação com comprovantes",
          "Validação automática",
          "Aprovação do gestor",
          "Conferência financeira",
          "Autorização de pagamento",
          "Integração com folha"
        ]
      }
    },
    {
      id: "fluxos-financeiros",
      icon: TrendingUp,
      title: "Fluxos Financeiros Automatizados",
      description: "Automação de todos os processos financeiros com integrações ERP",
      color: "from-fs-purple-500 to-fs-magenta-500",
      details: {
        overview: "Automatize completamente seus fluxos financeiros com integração nativa aos principais ERPs do mercado, garantindo que todas as transações sejam processadas de forma eficiente, segura e com total rastreabilidade.",
        features: [
          "Automação de contas a pagar/receber",
          "Integração com ERPs (SAP, TOTVS, Oracle)",
          "Conciliação bancária automática",
          "Gestão de fluxo de caixa",
          "Relatórios gerenciais avançados",
          "Dashboard financeiro em tempo real"
        ],
        benefits: [
          "Redução de erros manuais",
          "Processamento 24/7 automatizado",
          "Visibilidade financeira completa",
          "Integração total com sistemas"
        ],
        processes: [
          "Captura automática de dados",
          "Processamento por regras",
          "Validação e conferência",
          "Integração com ERP",
          "Atualização de saldos",
          "Geração de relatórios"
        ]
      }
    }
  ];

  const activeService = services.find(s => s.id === activeTab);

  const stats = [
    { icon: DollarSign, number: "R$ 10M+", label: "Transacionado/mês" },
    { icon: Clock, number: "70%", label: "Redução de Tempo" },
    { icon: Shield, number: "99.9%", label: "Precisão" },
    { icon: TrendingUp, number: "500%", label: "ROI Médio" }
  ];

  const financialBenefits = [
    {
      icon: Calculator,
      title: "Controle Orçamentário",
      description: "Acompanhamento em tempo real do orçamento com alertas automáticos de desvios"
    },
    {
      icon: Shield,
      title: "Compliance Financeiro",
      description: "Garantia de aderência às políticas internas e regulamentações fiscais"
    },
    {
      icon: BarChart3,
      title: "Relatórios Avançados",
      description: "Dashboards e relatórios que oferecem visão completa da situação financeira"
    },
    {
      icon: Zap,
      title: "Automação Total",
      description: "Processos financeiros automatizados que eliminam trabalho manual"
    }
  ];

  const integrations = [
    {
      name: "SAP",
      description: "Integração nativa com módulos financeiros",
      icon: Globe
    },
    {
      name: "TOTVS Protheus",
      description: "Sincronização automática de dados",
      icon: Archive
    },
    {
      name: "Oracle Financials",
      description: "Conectividade via APIs REST",
      icon: FileSpreadsheet
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Automação Financeira com Fluig - Processos Digitais"
        description="Automatize compras, fornecedores, reembolsos e fluxos financeiros. Integração ERP, redução de 70% no tempo de aprovação e ROI de 500%. R$ 10M+ transacionados mensalmente."
        keywords={['automação financeira fluig', 'solicitação compras digital', 'reembolso despesas fluig', 'gestão fornecedores', 'integração erp financeiro', 'processos financeiros digitais', 'workflow financeiro']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-fs-purple-500/10 backdrop-blur-sm border border-fs-purple-500/30 rounded-full px-6 py-3 mb-8">
                <DollarSign className="w-5 h-5 text-fs-purple-400" />
                <span className="text-foreground dark:text-white/90 font-medium">
                  Processos Financeiros
                </span>
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-6xl font-bold font-poppins text-foreground dark:text-white mb-6">
                Automação
                <span className="block fs-gradient-text mt-2">
                  Financeira Completa
                </span>
              </h1>

              {/* Description */}
              <p className="text-xl text-muted-foreground dark:text-white/70 leading-relaxed mb-12 max-w-3xl mx-auto">
                Automatize todos os processos financeiros da sua empresa: desde solicitação
                de compras até reembolsos, com integração total aos sistemas ERP.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                {stats.map((stat, index) => (
                  <div key={stat.label} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 mb-3">
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-2xl md:text-3xl font-bold fs-gradient-text mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-muted-foreground dark:text-white/70">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Button variant="fs-primary" size="lg" className="group">
                  Solicitar Análise
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="fs-outline" size="lg">
                  Ver ROI Calculado
                </Button>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/30">
          <Container>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Vantagens da Automação
                <span className="block fs-gradient-text mt-2">
                  Financeira
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Transforme seu departamento financeiro com automação inteligente e controle total.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {financialBenefits.map((benefit, index) => (
                <Card key={benefit.title} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:-translate-y-2 transition-all duration-500">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 mb-4">
                      <benefit.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Integrations Section */}
        <section className="py-20 bg-fs-section">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Integrações
                <span className="block fs-gradient-text mt-2">
                  ERP Nativas
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-muted-foreground dark:text-white/80 max-w-2xl mx-auto mb-8">
                Conecte-se aos principais sistemas ERP do mercado com integrações pré-configuradas.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {integrations.map((integration, index) => (
                <Card key={integration.name} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:-translate-y-2 transition-all duration-500">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 mb-4">
                      <integration.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">
                      {integration.name}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 text-sm">
                      {integration.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Services Navigation */}
        <section className="py-12 bg-fs-section border-b border-border">
          <Container>
            <div className="flex flex-wrap justify-center gap-4">
              {services.map((service) => {
                const IconComponent = service.icon;
                return (
                  <button
                    key={service.id}
                    onClick={() => setActiveTab(service.id)}
                    className={`flex items-center space-x-3 px-6 py-3 rounded-xl transition-all duration-300 ${
                      activeTab === service.id
                        ? 'bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 text-white scale-105'
                        : 'bg-card dark:bg-gray-900/50 text-foreground dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-gray-900/70'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="font-medium">{service.title}</span>
                  </button>
                );
              })}
            </div>
          </Container>
        </section>

        {/* Active Service Details */}
        {activeService && (
          <section className="py-20 bg-fs-section">
            <Container>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Service Info */}
                <div>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${activeService.color} p-4`}>
                      <activeService.icon className="w-full h-full text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold font-poppins text-foreground dark:text-white">
                        {activeService.title}
                      </h2>
                      <p className="text-fs-purple-400">{activeService.description}</p>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {/* Overview */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-foreground dark:text-white mb-4">Como Funciona</h3>
                      <p className="text-muted-foreground dark:text-white/70 leading-relaxed">
                        {activeService.details.overview}
                      </p>
                    </div>

                    {/* Features */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-foreground dark:text-white mb-4">Principais Funcionalidades</h3>
                      <ul className="space-y-3">
                        {activeService.details.features.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-fs-purple-400 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground dark:text-white/70">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Process Flow */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-foreground dark:text-white mb-4">Fluxo do Processo</h3>
                      <div className="space-y-3">
                        {activeService.details.processes.map((process, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 flex items-center justify-center text-white text-xs font-bold">
                              {index + 1}
                            </div>
                            <span className="text-muted-foreground dark:text-white/70">{process}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Benefits and ROI */}
                <div className="space-y-8">
                  {/* Benefits Card */}
                  <Card className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-3 text-foreground dark:text-white">
                        <Zap className="w-6 h-6 text-fs-purple-400" />
                        <span>Principais Benefícios</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {activeService.details.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-fs-cyan-400 rounded-full flex-shrink-0 mt-2" />
                            <span className="text-muted-foreground dark:text-white/70">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* ROI Card */}
                  <Card className="bg-gradient-to-br from-fs-purple-500/20 to-fs-cyan-500/20 border border-fs-purple-500/30">
                    <CardContent className="p-6">
                      <div className="text-center mb-4">
                        <TrendingUp className="w-12 h-12 text-fs-purple-400 mx-auto mb-3" />
                        <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">
                          Retorno sobre Investimento
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold fs-gradient-text">500%</div>
                          <div className="text-xs text-muted-foreground dark:text-white/70">ROI em 18 meses</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold fs-gradient-text">70%</div>
                          <div className="text-xs text-muted-foreground dark:text-white/70">Redução de custos</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* CTA Card */}
                  <Card className="bg-gradient-to-br from-fs-cyan-500/20 to-fs-magenta-500/20 border border-fs-cyan-500/30">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-xl font-bold text-foreground dark:text-white mb-3">
                        Calcule sua economia
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 mb-6">
                        Nossos especialistas podem calcular exatamente quanto
                        sua empresa economizará com a automação.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button variant="fs-primary" className="flex-1">
                          <Calculator className="w-4 h-4 mr-2" />
                          Calcular ROI
                        </Button>
                        <Button variant="fs-outline" className="flex-1">
                          Agendar Reunião
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </Container>
          </section>
        )}

        {/* Cost Reduction Section */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Redução de Custos
                <span className="block fs-gradient-text mt-2">
                  Comprovada
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-white/80 max-w-2xl mx-auto mb-8">
                Veja como nossos clientes reduziram significativamente seus custos operacionais.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Redução de Tempo",
                  percentage: "70%",
                  description: "Menos tempo gasto em processos manuais",
                  icon: Clock,
                  color: "from-fs-purple-500 to-fs-cyan-500"
                },
                {
                  title: "Economia Operacional",
                  percentage: "45%",
                  description: "Redução nos custos operacionais gerais",
                  icon: PiggyBank,
                  color: "from-fs-cyan-500 to-fs-magenta-500"
                },
                {
                  title: "Eliminação de Erros",
                  percentage: "90%",
                  description: "Redução de erros em processos financeiros",
                  icon: Shield,
                  color: "from-fs-magenta-500 to-fs-purple-500"
                }
              ].map((metric, index) => (
                <Card key={metric.title} className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20 hover:-translate-y-2 transition-all duration-500">
                  <CardContent className="p-6 text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${metric.color} mb-4`}>
                      <metric.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-3xl font-bold fs-gradient-text mb-2">
                      {metric.percentage}
                    </div>
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-3">
                      {metric.title}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed">
                      {metric.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Other Services Section */}
        <section className="py-20 bg-fs-section">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Outros Serviços
                <span className="block fs-gradient-text mt-2">
                  Especializados
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Explore nossas outras áreas de expertise para uma solução completa.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Tecnologia da Informação",
                  description: "Outsourcing Fluig, desenvolvimento JavaScript/React e soluções Python.",
                  icon: MonitorPlay,
                  href: "/servicos/ti",
                  color: "from-fs-purple-500 to-fs-cyan-500"
                },
                {
                  title: "Recursos Humanos",
                  description: "Automação de processos de RH com admissão digital e gestão de férias.",
                  icon: Users,
                  href: "/servicos/rh",
                  color: "from-fs-cyan-500 to-fs-magenta-500"
                },
                {
                  title: "Gestão da Qualidade",
                  description: "Controle de não conformidades e gestão completa de documentos.",
                  icon: Target,
                  href: "/servicos/qualidade",
                  color: "from-fs-magenta-500 to-fs-purple-500"
                }
              ].map((service, index) => (
                <Link key={service.title} to={service.href}>
                  <Card className="group bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20 hover:-translate-y-2 transition-all duration-500 hover:shadow-fs-hover hover:border-fs-purple-500/50 h-full">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} p-3 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <service.icon className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground dark:text-white mb-3 group-hover:fs-gradient-text transition-all duration-300">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <Button variant="fs-outline" size="sm" className="w-full group-hover:bg-fs-primary/20">
                        Saiba Mais
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </Container>
        </section>

        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Explore Outros Serviços"
              links={[
                {
                  title: "Gestão da Qualidade",
                  description: "Controle não conformidades, gerencie documentos e monitore processos de qualidade automaticamente.",
                  href: "/servicos/qualidade",
                  category: "Serviços"
                },
                {
                  title: "Serviços de TI Especializados",
                  description: "Outsourcing Fluig, desenvolvimento JavaScript/React e soluções Python com suporte 24/7.",
                  href: "/servicos/ti",
                  category: "Serviços"
                },
                {
                  title: "Automação de Processos",
                  description: "Transforme processos manuais em workflows automatizados com nossa expertise em BPM e Fluig.",
                  href: "/automacao-processos",
                  category: "Automação"
                },
                {
                  title: "Consultoria Fluig",
                  description: "Otimize sua plataforma Fluig com consultoria estratégica e 50.000+ horas de experiência comprovada.",
                  href: "/consultoria-fluig",
                  category: "Consultoria"
                }
              ]}
            />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <Container>
            <FAQSection 
              title="Perguntas Frequentes - Processos Financeiros"
              description="Esclareça suas dúvidas sobre nossos serviços de Processos Financeiros"
              faqs={[
                {
                  question: "Como funciona o processo de compras automatizado?",
                  answer: "Nosso processo de compras totalmente digital começa com solicitação através de catálogo de produtos pré-aprovados ou requisição livre. O sistema valida automaticamente o orçamento disponível e encaminha para aprovação hierárquica baseada em regras configuráveis. Após aprovação, cotações são solicitadas automaticamente aos fornecedores cadastrados. O sistema compara preços, prazos e condições, gerando relatórios para tomada de decisão. Todo o fluxo é rastreável e integra com sistemas ERP."
                },
                {
                  question: "O sistema de gestão de fornecedores é seguro?",
                  answer: "Sim, utilizamos criptografia de ponta a ponta e certificação digital para validação de documentos. Fornecedores acessam portal seguro com autenticação multi-fator para atualizar dados e documentos. O sistema valida automaticamente CNPJ, inscrições estaduais e certidões através de APIs oficiais. Mantemos backup redundante e auditoria completa de todas as alterações. Atendemos aos padrões LGPD e oferecemos controle granular de acesso por perfil de usuário."
                },
                {
                  question: "Como é feita a integração com sistemas ERP existentes?",
                  answer: "Possuímos conectores nativos para SAP, TOTVS Protheus, Oracle Financials e outros ERPs através de APIs REST padronizadas. A sincronização ocorre em tempo real ou por lotes agendados, conforme configuração. Dados de fornecedores, centros de custo, contas contábeis e ordens de compra são sincronizados automaticamente. Oferecemos período de teste em ambiente separado e migração assistida com equipe especializada, garantindo zero impacto nas operações."
                },
                {
                  question: "Qual o processo para aprovação de reembolsos de despesas?",
                  answer: "Colaboradores fotografam comprovantes pelo app mobile ou fazem upload no portal web. O sistema valida automaticamente valores contra políticas empresariais (limites por categoria, necessidade de justificativa). Despesas não conformes são sinalizadas para análise manual. Gestores recebem notificações e podem aprovar com um clique. Após aprovação, dados seguem automaticamente para a folha de pagamento ou sistema de contas a pagar. O processo completo leva menos de 48 horas."
                },
                {
                  question: "Como funcionam os controles e relatórios financeiros?",
                  answer: "O sistema oferece dashboards em tempo real com indicadores financeiros configuráveis: budget vs realizado, análise de gastos por centro de custo, performance de fornecedores e ciclo de aprovações. Relatórios podem ser agendados e enviados automaticamente por email. Inclui análises de tendências, alertas de desvios orçamentários e projeções baseadas em histórico. Dados podem ser exportados para Excel/PDF ou visualizados em gráficos interativos com drill-down por período, departamento ou categoria."
                },
                {
                  question: "Existe controle de alçadas e aprovações financeiras no sistema?",
                  answer: "Sim, o sistema possui engine de aprovações totalmente configurável por valor, categoria, centro de custo e hierarquia organizacional. Permite múltiplos níveis de aprovação com regras complexas (ex: valores acima de R$ 10mil precisam de 2 aprovadores). Aprovadores podem delegar temporariamente suas alçadas e o sistema suporta aprovação em paralelo ou sequencial. Todas as aprovações são rastreadas com timestamp e podem ser revogadas se necessário. Inclui alertas automáticos para pendências e prazos vencidos."
                }
              ]}
            />
          </Container>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default ServicesFinanceiro;